// src/components/maintenance/ComparativaPrecios.tsx

import React, { useState, useEffect, useMemo } from 'react';
import {
    Search,
    TrendingUp,
    Package,
    Clock,
    CheckCircle2,
    Plus,
    X,
    Save,
    Trash2,
    LayoutGrid,
    Filter,
    ArrowRight,
    ClipboardList,
    AlertTriangle
} from 'lucide-react';
import { maintenanceService } from '../../services/maintenance/planMaestroService';
import { proveedorService } from '../../services/maintenance/proveedorService';
import type { ItemCatalogo } from '../../types/planMaestro';
import toast from 'react-hot-toast';

interface Cotizacion {
    id: number;
    proveedor: {
        id: number;
        razon_social: string;
        calificacion: string;
        tiempo_respuesta_promedio_horas: number;
    };
    precio_unitario: number;
    tiempo_entrega_dias: number;
    stock_disponible: number;
    es_preferido: boolean;
    metricas_proveedor: {
        total_pedidos: number;
        entregas_a_tiempo: number;
        porcentaje_puntualidad: number;
        calidad_promedio: number;
        total_comprado: number;
    };
    score_total: number;
}

interface ProveedorSimple {
    id: number;
    razon_social: string;
}

const STORAGE_KEY = 'vilox_comparativa_items';

const ComparativaPrecios: React.FC = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [items, setItems] = useState<ItemCatalogo[]>([]);
    const [filteredItems, setFilteredItems] = useState<ItemCatalogo[]>([]);

    // Multi-item architecture
    const [selectedItems, setSelectedItems] = useState<ItemCatalogo[]>([]);
    const [quotesByItem, setQuotesByItem] = useState<Record<number, Cotizacion[]>>({});
    const [loadingByItem, setLoadingByItem] = useState<Record<number, boolean>>({});

    // For Modal context
    const [activeItemForModal, setActiveItemForModal] = useState<ItemCatalogo | null>(null);
    const [showCatalogExplorer, setShowCatalogExplorer] = useState(false);

    const [proveedores, setProveedores] = useState<ProveedorSimple[]>([]);

    // Loading states


    // UI states
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [showQuoteModal, setShowQuoteModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Explorer Filter States
    const [explorerSearch, setExplorerSearch] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<string>('Todos');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [pendingDelete, setPendingDelete] = useState<{ item: ItemCatalogo, cotId: number } | null>(null);

    // Form state
    const [quoteForm, setQuoteForm] = useState({
        proveedor_id: '',
        precio_unitario: '',
        tiempo_entrega_dias: '3',
        stock_disponible: '0',
        fecha_vigencia_desde: new Date().toISOString().split('T')[0],
        observaciones: ''
    });

    useEffect(() => {
        loadItems();
        loadProveedores();

        // Cargar ítems seleccionados desde localStorage
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            try {
                const parsedItems: ItemCatalogo[] = JSON.parse(saved);
                if (Array.isArray(parsedItems) && parsedItems.length > 0) {
                    setSelectedItems(parsedItems);
                    // Cargar cotizaciones para cada item recuperado
                    parsedItems.forEach(item => {
                        loadCotizaciones(item.id);
                    });
                }
            } catch (e) {
                console.error("Error recuperando comparativa guardada", e);
            }
        }
    }, []);

    // Guardar en localStorage cuando cambien los items seleccionados
    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedItems));
    }, [selectedItems]);

    useEffect(() => {
        if (searchTerm.length > 1) {
            const lowerTerm = searchTerm.toLowerCase();
            const filtered = items.filter(item => {
                // Filtro: Mostrar si es de la empresa O si está en uso en planes
                const isRelevant = item.empresa_id != null || item.en_uso_plan;
                if (!isRelevant) return false;

                return item.nombre.toLowerCase().includes(lowerTerm) ||
                    item.codigo_interno.toLowerCase().includes(lowerTerm);
            }).slice(0, 10);
            setFilteredItems(filtered);
            setShowSuggestions(true);
        } else {
            setFilteredItems([]);
            setShowSuggestions(false);
        }
    }, [searchTerm, items]);

    // Derived state for categories
    const categories = useMemo(() => {
        // Categorías de ítems relevantes
        const relevantItems = items.filter(i => i.empresa_id != null || i.en_uso_plan);
        const cats = new Set(relevantItems.map(i => i.categoria).filter(Boolean));
        return ['Todos', ...Array.from(cats)];
    }, [items]);

    // Filtered items for Explorer
    const explorerItems = useMemo(() => {
        return items.filter(item => {
            // Filtro: Mostrar si es de la empresa O si está en uso en planes
            const isRelevant = item.empresa_id != null || item.en_uso_plan;
            if (!isRelevant) return false;

            const matchSearch = item.nombre.toLowerCase().includes(explorerSearch.toLowerCase()) ||
                item.codigo_interno.toLowerCase().includes(explorerSearch.toLowerCase());
            const matchCategory = selectedCategory === 'Todos' || item.categoria === selectedCategory;
            return matchSearch && matchCategory;
        });
    }, [items, explorerSearch, selectedCategory]);


    const loadItems = async () => {
        try {

            const response = await maintenanceService.getItemsCatalogo();
            if (response.success && Array.isArray(response.data)) {
                setItems(response.data);
            }
        } catch (error) {
            console.error('Error cargando ítems:', error);
            toast.error('Error al cargar catálogo');

        }
    };

    const loadProveedores = async () => {
        try {
            const response = await proveedorService.getProveedores();
            if (response.success) {
                setProveedores(response.data);
            }
        } catch (error) {
            console.error('Error cargando proveedores:', error);
        }
    };

    const handleSelectItem = (item: ItemCatalogo) => {
        // Check if already selected
        if (selectedItems.some(i => i.id === item.id)) {
            toast('El ítem ya está en la lista', { icon: 'ℹ️' }); // Less annoying toast
            setSearchTerm('');
            setShowSuggestions(false);
            return;
        }

        setSelectedItems(prev => [item, ...prev]); // Add to top
        setSearchTerm('');
        setShowSuggestions(false);
        loadCotizaciones(item.id);
        toast.success('Agregado a comparativa');
    };

    const handleRemoveItem = (itemId: number) => {
        setSelectedItems(prev => prev.filter(i => i.id !== itemId));
    };

    const loadCotizaciones = async (itemId: number) => {
        try {
            setLoadingByItem(prev => ({ ...prev, [itemId]: true }));
            const response = await proveedorService.getCotizacionesParaItem(itemId);
            if (response.success) {
                setQuotesByItem(prev => ({ ...prev, [itemId]: response.data }));
            }
        } catch (error) {
            console.error('Error cargando cotizaciones:', error);
            // Silent error mostly
        } finally {
            setLoadingByItem(prev => ({ ...prev, [itemId]: false }));
        }
    };

    const handleOpenAddQuote = (item: ItemCatalogo) => {
        setActiveItemForModal(item);
        setShowQuoteModal(true);
    };

    const handleMarcarPreferido = async (item: ItemCatalogo, proveedorId: number) => {
        try {
            const response = await proveedorService.marcarProveedorPreferido(item.id, proveedorId);
            if (response.success) {
                toast.success('Proveedor preferido actualizado');
                loadCotizaciones(item.id);
            }
        } catch (error) {
            toast.error('Error al actualizar preferencia');
        }
    };

    const handleDeleteCotizacion = (item: ItemCatalogo, cotId: number) => {
        setPendingDelete({ item, cotId });
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        if (!pendingDelete) return;

        try {
            setSubmitting(true);
            await proveedorService.deleteCotizacion(pendingDelete.cotId);
            toast.success('Precio eliminado');
            loadCotizaciones(pendingDelete.item.id);
            setShowDeleteConfirm(false);
            setPendingDelete(null);
        } catch (error) {
            toast.error('Error al eliminar');
        } finally {
            setSubmitting(false);
        }
    };

    const handleSaveQuote = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!activeItemForModal || !quoteForm.proveedor_id || !quoteForm.precio_unitario) {
            toast.error('Complete los datos obligatorios');
            return;
        }

        try {
            setSubmitting(true);
            const payload = {
                item_catalogo_id: activeItemForModal.id,
                proveedor_id: parseInt(quoteForm.proveedor_id),
                precio_unitario: parseFloat(quoteForm.precio_unitario),
                tiempo_entrega_dias: parseInt(quoteForm.tiempo_entrega_dias),
                stock_disponible: parseInt(quoteForm.stock_disponible),
                fecha_vigencia_desde: quoteForm.fecha_vigencia_desde,
                observaciones: quoteForm.observaciones
            };

            await proveedorService.createCotizacion(payload);
            toast.success('Precio registrado');
            setShowQuoteModal(false);
            setQuoteForm({
                proveedor_id: '',
                precio_unitario: '',
                tiempo_entrega_dias: '3',
                stock_disponible: '0',
                fecha_vigencia_desde: new Date().toISOString().split('T')[0],
                observaciones: ''
            });
            loadCotizaciones(activeItemForModal.id);
        } catch (error) {
            console.error('Error guardando cotización:', error);
            toast.error('Error al guardar');
        } finally {
            setSubmitting(false);
        }
    };

    const getScoreColor = (score: number) => {
        if (score >= 80) return 'text-green-600 bg-green-50 border-green-200';
        if (score >= 60) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
        return 'text-red-600 bg-red-50 border-red-200';
    };

    return (
        <div className="p-6 max-w-[1600px] mx-auto space-y-6">

            {/* Toolbar Superior */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-col md:flex-row gap-4 items-center justify-between sticky top-4 z-30 transition-all">
                <div className="flex items-center gap-3">
                    <div className="bg-indigo-50 p-2.5 rounded-lg text-indigo-600 hidden md:block">
                        <TrendingUp size={20} />
                    </div>
                    <div>
                        <h2 className="text-lg font-bold text-gray-900 leading-tight">Comparativa de Precios</h2>
                        <p className="text-xs text-gray-500">
                            {selectedItems.length} ítem{selectedItems.length !== 1 ? 's' : ''} en análisis
                        </p>
                    </div>
                </div>

                {/* Buscador y Botón Catálogo */}
                <div className="flex-1 flex gap-2 max-w-2xl w-full">
                    <div className="relative flex-1 group">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-indigo-500 transition-colors" size={18} />
                        <input
                            type="text"
                            placeholder="Buscar ítem rápido..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onFocus={() => filteredItems.length > 0 && setShowSuggestions(true)}
                            className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all outline-none"
                        />

                        {/* Dropdown Sugerencias */}
                        {showSuggestions && filteredItems.length > 0 && (
                            <div className="absolute w-full mt-1 bg-white rounded-lg shadow-xl border border-gray-100 max-h-64 overflow-y-auto z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                                {filteredItems.map((item) => (
                                    <button
                                        key={item.id}
                                        onClick={() => handleSelectItem(item)}
                                        className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center justify-between group transition-colors border-b border-gray-50 last:border-0"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="bg-gray-100 p-1.5 rounded text-gray-500">
                                                <Package size={16} />
                                            </div>
                                            <div>
                                                <p className="font-medium text-gray-900 text-sm group-hover:text-indigo-600 transition-colors">{item.nombre}</p>
                                                <p className="text-xs text-gray-500">{item.codigo_interno}</p>
                                            </div>
                                        </div>
                                        <Plus size={16} className="text-gray-300 group-hover:text-indigo-600 transition-colors" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <button
                        onClick={() => setShowCatalogExplorer(true)}
                        className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-indigo-50 hover:text-indigo-600 transition-all font-medium text-sm whitespace-nowrap border border-gray-200 border-transparent hover:border-indigo-100"
                    >
                        <LayoutGrid size={18} />
                        <span className="hidden sm:inline">Explorar Catálogo</span>
                    </button>
                </div>

                <div className="w-full md:w-auto text-right">
                    {selectedItems.length > 0 && (
                        <button
                            onClick={() => setSelectedItems([])}
                            className="text-xs font-medium text-red-500 hover:text-red-700 hover:bg-red-50 px-3 py-1.5 rounded transition-colors"
                        >
                            Limpiar Todo
                        </button>
                    )}
                </div>
            </div>

            {/* Listado de Ítems Seleccionados */}
            <div className="space-y-6">
                {selectedItems.length > 0 ? (
                    selectedItems.map((item) => {
                        const itemQuotes = quotesByItem[item.id] || [];
                        const isLoading = loadingByItem[item.id];

                        return (
                            <div key={item.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-300">
                                {/* Encabezado del Ítem Row */}
                                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 bg-gray-50/50 border-b border-gray-100 gap-4">
                                    <div className="flex gap-4 items-start">
                                        <div className="h-10 w-10 bg-white rounded-lg border border-gray-200 mt-1 flex items-center justify-center text-indigo-600 shadow-sm shrink-0">
                                            <Package size={20} />
                                        </div>
                                        <div>
                                            <h3 className="text-base font-bold text-gray-900 leading-tight">{item.nombre}</h3>
                                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 mt-1">
                                                <span className="font-mono bg-white border border-gray-200 px-1.5 py-0.5 rounded text-gray-600">
                                                    {item.codigo_interno}
                                                </span>
                                                <span>{item.categoria}</span>
                                                <span className="text-gray-300">|</span>
                                                <span>{item.unidad_medida}</span>
                                            </div>

                                            {/* Planes y Rutinas asociadas */}
                                            {(item.planes_asociados?.length || 0) > 0 && (
                                                <div className="flex flex-wrap items-center gap-2 mt-2.5">
                                                    <div className="flex items-center gap-1 px-2 py-0.5 bg-amber-50 border border-amber-100 rounded text-[9px] font-black text-amber-700 uppercase tracking-tight">
                                                        <ClipboardList size={11} />
                                                        {item.planes_asociados?.length} {item.planes_asociados?.length === 1 ? 'Plan' : 'Planes'}
                                                    </div>

                                                    {item.rutinas_asociadas && item.rutinas_asociadas.length > 0 && (
                                                        <div className="flex flex-wrap gap-1">
                                                            {item.rutinas_asociadas.slice(0, 4).map((rutina, idx) => (
                                                                <span key={idx} className="px-2 py-0.5 bg-indigo-50 text-indigo-700 text-[9px] font-bold rounded border border-indigo-100/50">
                                                                    {rutina}
                                                                </span>
                                                            ))}
                                                            {item.rutinas_asociadas.length > 4 && (
                                                                <span className="text-[9px] text-gray-400 font-bold px-1">
                                                                    +{item.rutinas_asociadas.length - 4} más
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 self-end sm:self-auto">
                                        <button
                                            onClick={() => handleOpenAddQuote(item)}
                                            className="flex items-center gap-1.5 bg-indigo-600 text-white px-3 py-1.5 rounded-lg hover:bg-indigo-700 transition-colors text-xs font-bold shadow-sm shadow-indigo-200"
                                        >
                                            <Plus size={14} />
                                            Agregar Precio
                                        </button>
                                        <div className="h-6 w-px bg-gray-300 mx-1"></div>
                                        <button
                                            onClick={() => handleRemoveItem(item.id)}
                                            className="text-gray-400 hover:text-red-500 p-1.5 rounded-md hover:bg-red-50 transition-colors"
                                            title="Quitar de la lista"
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>
                                </div>

                                {/* Tabla de Resultados */}
                                <div>
                                    {isLoading ? (
                                        <div className="p-6 flex justify-center py-8">
                                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                                        </div>
                                    ) : itemQuotes.length > 0 ? (
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-sm text-left">
                                                <thead className="bg-white text-gray-500 font-medium text-xs border-b border-gray-100">
                                                    <tr>
                                                        <th className="px-6 py-2.5 font-normal">Proveedor</th>
                                                        <th className="px-6 py-2.5 font-normal">Precio Unit.</th>
                                                        <th className="px-6 py-2.5 font-normal">Entrega / Stock</th>
                                                        <th className="px-6 py-2.5 font-normal text-center">Calidad</th>
                                                        <th className="px-6 py-2.5 font-normal text-right">Acción</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-50">
                                                    {(() => {
                                                        const minPrice = Math.min(...itemQuotes.map(c => Number(c.precio_unitario)));

                                                        return itemQuotes.map((cot) => {
                                                            const isBestPrice = Number(cot.precio_unitario) === minPrice;

                                                            return (
                                                                <tr
                                                                    key={cot.id}
                                                                    className={`group hover:bg-gray-50 transition-colors ${cot.es_preferido ? 'bg-indigo-50/20' : ''}`}
                                                                >
                                                                    <td className="px-6 py-3">
                                                                        <div className="flex items-center gap-2">
                                                                            {cot.es_preferido && <CheckCircle2 size={14} className="text-indigo-600" />}
                                                                            <span className={`font-medium ${cot.es_preferido ? 'text-indigo-900' : 'text-gray-900'}`}>
                                                                                {cot.proveedor.razon_social}
                                                                            </span>
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-6 py-3">
                                                                        <div className="flex flex-col">
                                                                            <span className={`font-bold text-base ${isBestPrice ? 'text-green-600' : 'text-gray-700'}`}>
                                                                                S/ {Number(cot.precio_unitario).toFixed(2)}
                                                                            </span>
                                                                            {isBestPrice && <span className="text-[10px] text-green-600 font-medium">Mejor Precio</span>}
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-6 py-3">
                                                                        <div className="flex flex-col gap-0.5 text-xs text-gray-500">
                                                                            <div className="flex items-center gap-1">
                                                                                <Clock size={12} /> {cot.tiempo_entrega_dias} días espera
                                                                            </div>
                                                                            <span className={cot.stock_disponible > 0 ? 'text-gray-600' : 'text-red-500'}>
                                                                                Stock: {cot.stock_disponible}
                                                                            </span>
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-6 py-3 text-center">
                                                                        <div className="flex flex-col items-center gap-1">
                                                                            <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold border ${getScoreColor(cot.score_total)}`}>
                                                                                Score: {cot.score_total}
                                                                            </span>
                                                                            <span className={`text-[10px] font-bold px-1 rounded border border-gray-200 bg-gray-50 text-gray-600`}>
                                                                                Nivel {cot.proveedor.calificacion}
                                                                            </span>
                                                                        </div>
                                                                    </td>
                                                                    <td className="px-6 py-3 text-right">
                                                                        <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                            {!cot.es_preferido && (
                                                                                <button
                                                                                    onClick={() => handleMarcarPreferido(item, cot.proveedor.id)}
                                                                                    className="text-indigo-600 bg-indigo-50 hover:bg-indigo-100 px-2 py-1 rounded text-xs font-medium transition-colors"
                                                                                >
                                                                                    Elegir
                                                                                </button>
                                                                            )}

                                                                            <button
                                                                                onClick={() => handleDeleteCotizacion(item, cot.id)}
                                                                                className="text-gray-400 hover:text-red-600 p-1.5 hover:bg-red-50 rounded transition-colors"
                                                                            >
                                                                                <Trash2 size={14} />
                                                                            </button>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        });
                                                    })()}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div className="text-center py-6">
                                            <p className="text-xs text-gray-500">No hay precios registrados.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })
                ) : (
                    /* Estado Inicial Limpio */
                    <div className="flex flex-col items-center justify-center h-[50vh] text-center opacity-70 animate-in fade-in duration-500">
                        <div className="bg-gradient-to-br from-indigo-50 to-blue-50 p-6 rounded-full mb-6 shadow-sm">
                            <LayoutGrid size={48} className="text-indigo-300" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Comienza la Comparativa</h3>
                        <p className="text-sm text-gray-500 max-w-md mx-auto mb-6">
                            Utiliza el buscador o explora el catálogo para añadir ítems y comparar las ofertas de tus proveedores en tiempo real.
                        </p>
                        <button
                            onClick={() => setShowCatalogExplorer(true)}
                            className="bg-white border border-gray-200 text-gray-700 hover:text-indigo-600 hover:border-indigo-200 px-6 py-2.5 rounded-lg shadow-sm font-medium transition-all flex items-center gap-2"
                        >
                            <LayoutGrid size={18} />
                            Ver Catálogo Completo
                        </button>
                    </div>
                )}
            </div>

            {/* Modal Explorador de Catálogo */}
            {
                showCatalogExplorer && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
                        <div className="bg-white w-full max-w-5xl h-[90vh] rounded-2xl shadow-2xl flex flex-col animate-in zoom-in-95 duration-200 overflow-hidden">

                            {/* Header Explorer */}
                            <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-white sticky top-0 z-10">
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900">Catálogo de Repuestos y Servicios</h3>
                                    <p className="text-sm text-gray-500">Selecciona los ítems que deseas comparar</p>
                                </div>
                                <button
                                    onClick={() => setShowCatalogExplorer(false)}
                                    className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-600"
                                >
                                    <X size={24} />
                                </button>
                            </div>

                            <div className="flex flex-1 overflow-hidden">
                                {/* Sidebar Categorias */}
                                <div className="w-64 border-r border-gray-200 bg-gray-50/50 p-4 overflow-y-auto hidden md:block">
                                    <h4 className="font-semibold text-xs text-gray-500 uppercase tracking-wider mb-3">Categorías</h4>
                                    <div className="space-y-1">
                                        {categories.map(cat => (
                                            <button
                                                key={cat}
                                                onClick={() => setSelectedCategory(cat)}
                                                className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors flex justify-between items-center ${selectedCategory === cat
                                                    ? 'bg-indigo-50 text-indigo-700'
                                                    : 'text-gray-600 hover:bg-gray-100'
                                                    }`}
                                            >
                                                <span className="truncate">{cat}</span>
                                                {selectedCategory === cat && <div className="w-1.5 h-1.5 rounded-full bg-indigo-500"></div>}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Main Content Explorer */}
                                <div className="flex-1 flex flex-col bg-white">
                                    {/* Search Bar inside Explorer */}
                                    <div className="p-4 border-b border-gray-100 flex gap-4 items-center">
                                        <div className="relative flex-1">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                            <input
                                                type="text"
                                                placeholder="Buscar en el catálogo..."
                                                value={explorerSearch}
                                                onChange={(e) => setExplorerSearch(e.target.value)}
                                                className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                                            />
                                        </div>
                                        <div className="md:hidden">
                                            <button className="p-2 bg-gray-100 rounded-lg text-gray-600">
                                                <Filter size={18} />
                                            </button>
                                            {/* Mobile filter dropdown could go here */}
                                        </div>
                                    </div>

                                    {/* Items Grid */}
                                    <div className="flex-1 overflow-y-auto p-4">
                                        {explorerItems.length > 0 ? (
                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                                {explorerItems.map(item => {
                                                    const isSelected = selectedItems.some(si => si.id === item.id);
                                                    return (
                                                        <div
                                                            key={item.id}
                                                            onClick={() => !isSelected && handleSelectItem(item)}
                                                            className={`border rounded-xl p-4 transition-all cursor-pointer relative group ${isSelected
                                                                ? 'border-indigo-200 bg-indigo-50/30'
                                                                : 'border-gray-200 hover:border-indigo-300 hover:shadow-md bg-white'
                                                                }`}
                                                        >
                                                            <div className="flex justify-between items-start mb-2">
                                                                <div className="bg-gray-100 p-2 rounded-lg text-gray-500 group-hover:text-indigo-600 transition-colors">
                                                                    <Package size={20} />
                                                                </div>
                                                                {isSelected ? (
                                                                    <span className="flex items-center gap-1 text-xs font-bold text-indigo-600 bg-indigo-100 px-2 py-1 rounded-full">
                                                                        <CheckCircle2 size={12} /> Agregado
                                                                    </span>
                                                                ) : (
                                                                    <button className="text-gray-400 group-hover:text-indigo-600 transition-colors">
                                                                        <Plus size={20} />
                                                                    </button>
                                                                )}
                                                            </div>
                                                            <h4 className="font-bold text-gray-900 mb-1 line-clamp-1" title={item.nombre}>{item.nombre}</h4>
                                                            <p className="text-xs text-gray-500 mb-3 font-mono">{item.codigo_interno}</p>

                                                            <div className="flex items-center justify-between text-[10px] text-gray-500 pt-3 border-t border-gray-100">
                                                                <span>{item.categoria}</span>
                                                                {item.planes_asociados && item.planes_asociados.length > 0 ? (
                                                                    <span className="text-amber-600 font-bold">
                                                                        {item.planes_asociados.length} Plan{item.planes_asociados.length !== 1 ? 'es' : ''}
                                                                    </span>
                                                                ) : (
                                                                    <span>{item.unidad_medida}</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <div className="flex flex-col items-center justify-center h-full text-center py-12">
                                                <Search size={48} className="text-gray-200 mb-4" />
                                                <p className="text-gray-500 font-medium">No se encontraron ítems</p>
                                                <p className="text-sm text-gray-400">Intenta con otros términos o categoría</p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Footer Explorer */}
                                    <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
                                        <span className="text-sm text-gray-600">
                                            Mostrando {explorerItems.length} resultados
                                        </span>
                                        {selectedItems.length > 0 && (
                                            <button
                                                onClick={() => setShowCatalogExplorer(false)}
                                                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium text-sm flex items-center gap-2 shadow-sm shadow-indigo-200"
                                            >
                                                Ir a Comparativa <ArrowRight size={16} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Modal Agregar Cotización */}
            {
                showQuoteModal && activeItemForModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4 backdrop-blur-sm">
                        <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                            <div className="flex justify-between items-center p-4 border-b border-gray-100 bg-gray-50">
                                <div>
                                    <h3 className="font-bold text-gray-900">Registrar Precio</h3>
                                    <p className="text-xs text-gray-500 truncate max-w-[300px]">{activeItemForModal.nombre}</p>
                                </div>
                                <button onClick={() => setShowQuoteModal(false)} className="text-gray-400 hover:text-gray-600">
                                    <X size={20} />
                                </button>
                            </div>

                            <form onSubmit={handleSaveQuote} className="p-6 space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Proveedor *</label>
                                    <select
                                        required
                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 transition-all outline-none"
                                        value={quoteForm.proveedor_id}
                                        onChange={e => setQuoteForm({ ...quoteForm, proveedor_id: e.target.value })}
                                    >
                                        <option value="">Seleccione un proveedor...</option>
                                        {proveedores.map(prov => (
                                            <option key={prov.id} value={prov.id}>{prov.razon_social}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Precio Unit. (S/) *</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            required
                                            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                                            value={quoteForm.precio_unitario}
                                            onChange={e => setQuoteForm({ ...quoteForm, precio_unitario: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Stock Disp.</label>
                                        <input
                                            type="number"
                                            min="0"
                                            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                                            value={quoteForm.stock_disponible}
                                            onChange={e => setQuoteForm({ ...quoteForm, stock_disponible: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Entrega (días) *</label>
                                        <input
                                            type="number"
                                            min="1"
                                            required
                                            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                                            value={quoteForm.tiempo_entrega_dias}
                                            onChange={e => setQuoteForm({ ...quoteForm, tiempo_entrega_dias: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Vigencia Desde</label>
                                        <input
                                            type="date"
                                            required
                                            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                                            value={quoteForm.fecha_vigencia_desde}
                                            onChange={e => setQuoteForm({ ...quoteForm, fecha_vigencia_desde: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Observaciones</label>
                                    <textarea
                                        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none resize-none h-20"
                                        value={quoteForm.observaciones}
                                        onChange={e => setQuoteForm({ ...quoteForm, observaciones: e.target.value })}
                                    />
                                </div>

                                <div className="pt-2 flex gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setShowQuoteModal(false)}
                                        className="flex-1 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium transition-colors"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 test-sm font-medium shadow-sm transition-colors flex items-center justify-center gap-2"
                                    >
                                        {submitting ? 'Guardando...' : <><Save size={16} /> Guardar</>}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )
            }

            {/* Modal Confirmación de Eliminación */}
            {
                showDeleteConfirm && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[80] p-4 backdrop-blur-sm">
                        <div className="bg-white rounded-xl shadow-xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                            <div className="p-6 text-center">
                                <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-red-100">
                                    <AlertTriangle className="text-red-500" size={32} />
                                </div>
                                <h3 className="text-lg font-bold text-gray-900 mb-2">¿Eliminar este precio?</h3>
                                <p className="text-sm text-gray-500 mb-6">
                                    Esta acción no se puede deshacer y el precio dejará de estar disponible en la comparativa.
                                </p>
                                <div className="flex gap-3">
                                    <button
                                        onClick={() => {
                                            setShowDeleteConfirm(false);
                                            setPendingDelete(null);
                                        }}
                                        className="flex-1 px-4 py-2 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 text-sm font-medium transition-colors"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        onClick={confirmDelete}
                                        disabled={submitting}
                                        className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium shadow-sm transition-colors flex items-center justify-center gap-2"
                                    >
                                        {submitting ? 'Eliminando...' : 'Eliminar'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }
        </div>
    );
};

export default ComparativaPrecios;
